<template>
	<section id="values" class="section">
		<div class="container">
			<header v-reveal class="section__head">
				<p class="section__eyebrow">{{ t('values.eyebrow') }}</p>
				<h2 class="section__title">{{ t('values.title') }}</h2>
			</header>

			<div class="values__grid">
				<article
					v-for="(key, index) in VALUE_KEYS"
					:key="key"
					v-reveal="index * 60"
					class="hex"
				>
					<img
						:src="valueIconUrl(index)"
						class="hex__icon"
						alt=""
						loading="lazy"
					>
					<h3 class="hex__title">{{ t(`values.items.${key}.title`) }}</h3>
					<p class="hex__desc">{{ t(`values.items.${key}.desc`) }}</p>
				</article>
			</div>
		</div>
	</section>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { VALUE_KEYS } from '@/helpers/constants'
import { valueIconUrl } from '@/helpers/functions'

defineOptions({ name: 'ValuesSection' })

const { t } = useI18n()
</script>

<style scoped lang="scss">
.values__grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 268px), 1fr));
	gap: clamp(14px, 2vw, 22px);
}

// honeycomb cells, echoing the outlined hexagons in the printed profile
.hex {
	position: relative;
	padding: clamp(30px, 3.4vw, 42px) clamp(24px, 3vw, 34px);
	text-align: center;
	background: transparent;
	clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
	transition: transform 0.35s $ease;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		z-index: -1;
		background: $color-paper url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 220' preserveAspectRatio='none'%3E%3Cpolygon points='100,3 197,55 197,165 100,217 3,165 3,55' fill='%23ffffff' stroke='%230d0d0d' stroke-opacity='.5' stroke-width='2'/%3E%3C/svg%3E") center / 100% 100% no-repeat;
	}

	&:hover {
		transform: translateY(-5px);
	}

	// hexagons get too tight on narrow screens — fall back to cards
	@include below($bp-mobile) {
		clip-path: none;
		border-radius: $radius;
		padding: 28px 22px;

		&::before {
			background: $color-paper;
			border: 1px solid $color-line-strong;
			border-radius: $radius;
		}
	}
}

.hex__icon {
	width: 52px;
	height: 52px;
	margin: 0 auto 16px;
	object-fit: contain;
}

.hex__title {
	margin-bottom: 10px;
	font-size: clamp(1.08rem, 2vw, 1.32rem);
	font-weight: 800;
}

.hex__desc {
	max-width: 26ch;
	margin-inline: auto;
	font-size: 0.94rem;
	color: $color-body;
}
</style>
